import { useMemo, useState, type ReactNode } from 'react'
import useAnnouncements from '../../hooks/announcements/useAnnouncements'
import { AnnouncementVideoProvider } from '../../hooks/announcements/AnnouncementVideoContext'
import { useChallengeId } from '../../hooks/challenge/useChallengeId'
import {
  useHasEngagement,
  useRecordEngagement,
  useUserEngagements,
} from '../../hooks/engagement/useUserEngagements'
import FullscreenVideoOverlay from '../../ui/primitives/FullscreenVideoOverlay'

export default function AnnouncementOverlayContainer({ children }: { children: ReactNode }) {
  const challengeId = useChallengeId()
  const { data: announcements = [] } = useAnnouncements(challengeId)
  const { isError: engagementsUnavailable } = useUserEngagements()
  const { mutate: recordEngagement } = useRecordEngagement()
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set())
  const [manualOpenId, setManualOpenId] = useState<string | null>(null)

  const autoCandidateId = useMemo(() => {
    if (engagementsUnavailable) return undefined
    return announcements.find(
      (item) => item.fullscreen && item.videoUrl && !dismissedIds.has(item.id),
    )?.id
  }, [announcements, dismissedIds, engagementsUnavailable])

  const hasSeenAuto = useHasEngagement('announcement', autoCandidateId)
  const autoAnnouncement = announcements.find((item) => item.id === autoCandidateId)
  const manualAnnouncement = announcements.find((item) => item.id === manualOpenId)

  const autoActive =
    autoAnnouncement?.videoUrl && !hasSeenAuto ? autoAnnouncement : undefined
  const activeAnnouncement = manualAnnouncement?.videoUrl ? manualAnnouncement : autoActive

  function dismiss() {
    if (manualOpenId) {
      setManualOpenId(null)
      return
    }

    if (!activeAnnouncement) return

    setDismissedIds((prev) => new Set(prev).add(activeAnnouncement.id))
    recordEngagement({ subjectType: 'announcement', subjectID: activeAnnouncement.id })
  }

  return (
    <AnnouncementVideoProvider value={{ openAnnouncementVideo: setManualOpenId }}>
      {children}
      {activeAnnouncement?.videoUrl && (
        <FullscreenVideoOverlay
          videoUrl={activeAnnouncement.videoUrl}
          dismissible
          onClose={dismiss}
        />
      )}
    </AnnouncementVideoProvider>
  )
}
